/* eslint-disable max-len */
import { useEffect, useState } from 'react';
import './CompanyDropdownList.scss';
import { I18n } from 'react-redux-i18n';
import useGetAllSearchURLParams from '@helpers/customHooks/useGetAllSearchURLParams';
import { a2bEncoder } from '@helpers/getBase64EncodeDecode';
import { setHasNavigate, setApplicationState, setSelectedCompanyInfo } from '@redux/slices/appSlice';
import { isProductIsBusinessCreditPlatform } from '@landing/EntitledProducts/constant';
import { CompanyDetails, CredibilityEntitlement } from '@customTypes/ItemTypes';
import { get, sortBy } from 'lodash';
import { ALL_API_ENHANCEMENT } from '@helpers/constants';
import { useAppDispatch, useAppSelector } from '@redux/reduxHooks';
import { RootState } from '@redux/store';
import CompanySearchableDropdown from '../../company-details/CompanySearchableDropdown/CompanySearchableDropdown';
import CompanyDropdown from '../../company-details/CompanyDropdown/CompanyDropdown';

const unAssignedApp = {
    mapDetails: {
        countryCode: '',
        countryName: '',
        postalCode: '',
        streetAddress: '',
        territory: '',
        town: '',
        buildingName: ''
    },
    profileDetails: {
        companyProfileUrl: '',
        countryName: '',
        dunsNumber: '',
        industryDescription: '',
        legalForm: '',
        ownership: '',
        principal: '',
        standaloneIndicator: false,
        telephoneNumber: '',
        website: ''
    },
    scoring: {
        delinquency: 0,
        financialStress: 0,
        financialStressNationalPercentile: 0,
        paydex: 0,
        serRating: 0
    },
    summary: {
        bankruptcyIndicator: false,
        businessName: I18n.t('company-details:company-pending'),
        locationType: '',
        operatingStatus: '',
        reportDate: '',
        tradeName: '',
    }
};

export const CompanyDropdownList = ({ selectedCompanyData }:any) => {
    const dispatch = useAppDispatch();
    const companyInfo = useAppSelector((state: RootState) => state.app.companyInfo);
    const entitlements = useAppSelector((state: RootState) => state.app.entitlements);
    const isPendingOwlEntitlementsAvailable = useAppSelector((state: RootState) => state.app.entitlements.isPendingOwlEntitlementsAvailable);
    const dunsApplicationProgress = useAppSelector((state: RootState) => state.app.dunsApplicationProgress);
    const credibilityEntitlements = entitlements?.credibilityEntitlements;
    const [querySelectedCompanyInfo, setQueryCompanyInfo] = useState<CompanyDetails>();
    const { checkSpecificValueExist, getSpecificValue } = useGetAllSearchURLParams('RelayState');
    const storedSelectedCompanyInfo = localStorage.getItem('selectedCompanyInfo') !== 'undefined' && JSON.parse(localStorage.getItem('selectedCompanyInfo') as string);

    // Check credibilityEntitlements has any unassigned apps
    const isUnassignedApp = credibilityEntitlements?.some((credibilityEntitlements: CredibilityEntitlement) => credibilityEntitlements?.type !== 'legacy_found'
     && credibilityEntitlements.displayDuns === '' && !isProductIsBusinessCreditPlatform(credibilityEntitlements.title));

    // if Entitlement(s) has unassigned app(s) adding unAssignedApp to Company drop down list
    let companyInfoList = (isPendingOwlEntitlementsAvailable || isUnassignedApp || (dunsApplicationProgress && Object.entries(dunsApplicationProgress).length > 0)) ? [unAssignedApp, ...companyInfo] as CompanyDetails[] : [...companyInfo] as CompanyDetails[];
    companyInfoList = sortBy(companyInfoList, (companyDetails) => get(companyDetails, 'summary.businessName', '')?.toLowerCase());
    !companyInfoList.length && localStorage.removeItem('selectedCompanyInfo');
    const [selectedCompanyInfo, setSelectedCompanyInfoData] = useState(storedSelectedCompanyInfo || companyInfoList[0]);
    useEffect(() => {
        if (checkSpecificValueExist && getSpecificValue) {
            const decodeParam = a2bEncoder(getSpecificValue);
            const urlParams = new URLSearchParams(decodeParam).get('query');
            if (urlParams) {
                const selectedCompanyDetails:Array<CompanyDetails> = companyInfo?.filter((individualCompanyDetails: CompanyDetails) => individualCompanyDetails.profileDetails.dunsNumber === a2bEncoder(urlParams));
                if (!querySelectedCompanyInfo && selectedCompanyDetails && selectedCompanyDetails.length) {
                    setQueryCompanyInfo(selectedCompanyDetails[0]);
                    setSelectedCompanyInfoData(selectedCompanyDetails[0]);
                    dispatch(setSelectedCompanyInfo(selectedCompanyDetails[0]));
                    selectedCompanyData(selectedCompanyDetails[0]);
                }
            }
        }
    }, [companyInfo, getSpecificValue, checkSpecificValueExist]);

    useEffect(() => {
        dispatch(setSelectedCompanyInfo(selectedCompanyInfo || companyInfoList[0]));
        selectedCompanyData(selectedCompanyInfo || companyInfoList[0]);
    }, [companyInfo, companyInfoList, selectedCompanyInfo]);
    const onComponySelection = (index: number) => {
        dispatch(setHasNavigate({ deepLinkUrl: '', isNavigate: false, productName: '' }));
        dispatch(setApplicationState({
            isDunsSelected: false,
            isAllProductsVBO: false,
            isCompanyDropDownOnlyAvailable: false,
            isCompanyDropDownWithOutAnyVBOProducts: true,
            deepLinkUrl: '',
            isProductDPM: false
        }));
        setSelectedCompanyInfoData(companyInfoList[index]);
        dispatch(setSelectedCompanyInfo(companyInfoList[index]));
        selectedCompanyData(companyInfoList[index]);
    };
    // this useEffect handle only to update the company info  when the URL has the base64 DUNS number
    useEffect(() => {
        let companyValueToset = querySelectedCompanyInfo;
        if (companyValueToset?.profileDetails?.dunsNumber !== selectedCompanyInfo?.profileDetails?.dunsNumber) {
            companyValueToset = selectedCompanyInfo;
        }

        if (companyValueToset) {
            dispatch(setHasNavigate({ deepLinkUrl: '', isNavigate: false, productName: '' }));
            dispatch(setApplicationState({
                isDunsSelected: false,
                isAllProductsVBO: false,
                isCompanyDropDownOnlyAvailable: false,
                isCompanyDropDownWithOutAnyVBOProducts: true,
                deepLinkUrl: '',
                isProductDPM: false
            }));
            setSelectedCompanyInfoData(companyValueToset);
            dispatch(setSelectedCompanyInfo(companyValueToset));
        }
    }, [querySelectedCompanyInfo]);

    return (
        <>
            {companyInfoList.length > 0
            && (
                <span className="company-dropdown-container">
                    {ALL_API_ENHANCEMENT
                        ? (
                            <CompanySearchableDropdown
                                companyData={companyInfoList}
                                activeCompany={selectedCompanyInfo || companyInfoList[0]}
                                switchCompany={onComponySelection}
                            />
                        )
                        : (
                            <CompanyDropdown
                                companyData={companyInfoList}
                                activeCompany={selectedCompanyInfo || companyInfoList[0]}
                                switchCompany={onComponySelection}
                            />
                        )}
                </span>
            )}
        </>
    );
};
